import React from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, ArrowRight } from 'lucide-react';
import { NAV } from '@/constants/testIds';

const links = [
  { to: '/', label: 'Home', id: NAV.home },
  { to: '/services', label: 'Services', id: NAV.services },
  { to: '/case-studies', label: 'Case Studies', id: NAV.caseStudies },
  { to: '/about', label: 'About', id: NAV.about },
  { to: '/contact', label: 'Contact', id: NAV.contact },
];

export default function Navbar() {
  const [open, setOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);
  const location = useLocation();

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const closeMenu = React.useCallback(() => setOpen(false), []);

  const handleBookCall = (e) => {
    if (location.pathname === '/contact') {
      e.preventDefault();
      const el = document.getElementById('contact-form');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? 'backdrop-blur-xl bg-[#000919]/85 border-b border-white/10' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid={NAV.logo} className="flex items-center gap-3 group">
          <img
            src="/brand/sellrly-logotype-single.png"
            alt="Sellrly"
            className="h-8 md:h-9 w-auto transition-transform group-hover:scale-105"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === '/'}
              data-testid={l.id}
              className={({ isActive }) =>
                `px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  isActive
                    ? 'text-white bg-white/10'
                    : 'text-white/70 hover:text-white hover:bg-white/5'
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/contact#contact-form"
            data-testid={NAV.ctaButton}
            onClick={handleBookCall}
            className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-white bg-[#FF5081] hover:bg-[#ff3a73] hover:-translate-y-px shadow-[0_8px_22px_-12px_rgba(255,80,129,0.55)] transition-all"
          >
            Book a strategy call
            <ArrowRight className="w-4 h-4" />
          </Link>
          <button
            onClick={() => setOpen((s) => !s)}
            data-testid={NAV.mobileToggle}
            aria-label="Toggle menu"
            className="lg:hidden p-2 rounded-full text-white border border-white/15 hover:bg-white/10"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-white/10 bg-[#000919]/95 backdrop-blur-xl">
          <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-2">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === '/'}
                onClick={closeMenu}
                data-testid={`${l.id}-mobile`}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-2xl text-base font-medium ${
                    isActive ? 'text-white bg-white/10' : 'text-white/80 hover:bg-white/5'
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact#contact-form"
              className="mt-2 inline-flex justify-center items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-white bg-[#FF5081]"
            >
              Book a strategy call <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
